import { getJSON, postJSON } from 'common/services/api'
import { showToast } from 'common/utils'
import { useMutation, useQuery, useQueryClient } from 'react-query'
import { PaymentCard } from './usePaymentCards'
import {
  USER_SETTINGS_URL,
  USER_SETTINGS_QUERY_KEY,
  USER_SETTINGS_PAYRATE_UPDATE_QUERY_KEY
} from './queryConstants'

export interface UserPayrateVersion {
  payRateCardInitialId: string
  effectiveFrom: string
  supersededAt: string
}

export interface UserPayrate {
  payRateCardInitialId: string
  effectiveFrom: string
  createdAt: string
  updatedAt: string
  payRateCard?: PaymentCard
  versions: UserPayrateVersion[]
}

export interface UserSettings {
  _id: string
  homeId: string
  payrate: UserPayrate | null
  createdAt: string
  updatedAt: string
}

export interface UpdateUserPayrateDto {
  payRateCardInitialId: string
  effectiveFrom: string
}

function getPayrateUpdateErrorMessage(error: unknown): string {
  if (error instanceof Error) return error.message
  if (typeof error === 'object' && error !== null) {
    const e = error as { msg?: string | string[]; message?: string }
    if (Array.isArray(e.msg) && e.msg.length) {
      return e.msg.filter(Boolean).join(', ')
    }
    if (typeof e.msg === 'string') return e.msg
    if (typeof e.message === 'string') return e.message
  }
  return 'Something went wrong. Please try again.'
}

export const useGetUserSettings = (userId?: string) => {
  return useQuery<UserSettings>(
    [USER_SETTINGS_QUERY_KEY, userId],
    async () => {
      const response = await getJSON({
        url: `${USER_SETTINGS_URL}/${userId}`,
        isRota: true
      })
      return response
    },
    { enabled: !!userId }
  )
}

export const useUpdateUserPayrate = (userId?: string) => {
  const queryClient = useQueryClient()

  const { mutateAsync: updatePayrate, isLoading: isUpdating } = useMutation<
    UserSettings,
    unknown,
    UpdateUserPayrateDto
  >(
    [USER_SETTINGS_PAYRATE_UPDATE_QUERY_KEY, userId],
    async (dto) => {
      const response = await postJSON({
        url: `${USER_SETTINGS_URL}/${userId}/payrate`,
        method: 'PUT',
        json: dto,
        isRota: true
      })
      return response as UserSettings
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries([USER_SETTINGS_QUERY_KEY, userId])
      },
      onError: (error: unknown) => {
        showToast({
          type: 'error',
          title: getPayrateUpdateErrorMessage(error)
        })
      }
    }
  )

  return { updatePayrate, isUpdating }
}
